/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Grid,
  Switch,
  IconButton,
  Tooltip,
  Stack,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Box,
  Chip,
  Paper,
  Fade,
  Zoom,
} from "@mui/material";
import {
  Edit,
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon,
  EventNote,
  TrendingUp,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import axios from "axios";

// Your custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDTextField from "components/MDTextField";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

function HolidayAdd() {
  // State
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [holidays, setHolidays] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [holidayName, setHolidayName] = useState("");
  const [holidayDate, setHolidayDate] = useState(null);
  const [status, setStatus] = useState(1);
  const [errors, setErrors] = useState({});

  // Table State for Server-side Filtering
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch Holidays with Filters
  const fetchHolidays = async () => {
    setLoading(true);
    try {
      // Map MRT filters to API parameters
      const params = {};
      if (globalFilter) params.holiday_name = globalFilter;

      columnFilters.forEach((filter) => {
        params[filter.id] = filter.value;
      });

      const res = await axios.get(`${process.env.REACT_APP_API_URL}/holiday/`, {
        params,
        withCredentials: true,
      });
      if (res.data.status === 200) {
        setHolidays(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching holidays:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch holidays.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [columnFilters, globalFilter]);

  // Form Validation
  const validateForm = () => {
    const newErrors = {};
    if (!holidayName.trim()) newErrors.holidayName = "Holiday name is required.";
    if (!holidayDate) newErrors.holidayDate = "Holiday date is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = () => {
    setHolidayName("");
    setHolidayDate(null);
    setStatus(1);
    setIsEditMode(false);
    setEditingId(null);
    setErrors({});
  };

  // Submit Handler
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        holiday_name: holidayName,
        holiday_date: holidayDate.format("YYYY-MM-DD"),
        status: status,
      };

      let res;
      if (isEditMode) {
        res = await axios.put(
          `${process.env.REACT_APP_API_URL}/holiday/update/${editingId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        res = await axios.post(`${process.env.REACT_APP_API_URL}/holiday/add`, payload, {
          withCredentials: true,
        });
      }

      if (res.status === 200) {
        setSnackbar({
          open: true,
          message: res.data.message || (isEditMode ? "Holiday updated!" : "Holiday added!"),
          severity: "success",
        });
        clearForm();
        fetchHolidays();
      }
    } catch (err) {
      console.error("Submit error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.response?.data?.error || "Operation failed.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit
  const handleEdit = (row) => {
    setHolidayName(row.holiday_name);
    setHolidayDate(dayjs(row.holiday_date));
    setStatus(row.status);
    setEditingId(row.id);
    setIsEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle Status Toggle
  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/holiday/status/${id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setHolidays((prev) => prev.map((h) => (h.id === id ? { ...h, status: newStatus } : h)));
      setSnackbar({ open: true, message: "Status updated.", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update status.", severity: "error" });
    }
  };

  // Statistics
  const stats = useMemo(() => {
    const total = holidays.length;
    const active = holidays.filter((h) => h.status === 1).length;
    const upcoming = holidays.filter((h) => dayjs(h.holiday_date).isAfter(dayjs())).length;
    return { total, active, upcoming };
  }, [holidays]);

  // Columns for MRT
  const columns = useMemo(
    () => [
      {
        accessorKey: "holiday_date",
        header: "Holiday Date",
        Cell: ({ cell }) => (
          <Chip
            icon={<CalendarTodayIcon sx={{ fontSize: "14px" }} />}
            label={dayjs(cell.getValue()).format("DD MMM YYYY")}
            size="small"
            sx={{
              fontWeight: 500,
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
              "& .MuiChip-icon": { color: "#2e7d32" },
            }}
          />
        ),
        size: 120,
        Filter: ({ column }) => {
          const [open, setOpen] = useState(false);
          const filterValue = column.getFilterValue();
          // Fix: Parse YYYY-MM-DD from the filter state
          const value = filterValue ? dayjs(filterValue) : null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={value}
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                onChange={(newValue) => {
                  column.setFilterValue(
                    newValue ? dayjs(newValue).format("YYYY-MM-DD") : undefined
                  );
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    onClick: () => setOpen(true),
                    variant: "standard",
                    placeholder: "Filter Date",
                    InputProps: {
                      endAdornment: (
                        <React.Fragment>
                          {value && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                column.setFilterValue(undefined);
                              }}
                            >
                              <CloseIcon fontSize="small" sx={{ color: "#fff" }} />
                            </IconButton>
                          )}
                          <IconButton size="small" onClick={() => setOpen(true)}>
                            <CalendarTodayIcon fontSize="small" sx={{ color: "#fff" }} />
                          </IconButton>
                        </React.Fragment>
                      ),
                      sx: {
                        fontSize: "12px",


                        color: "#fff !important",


                        /* underline */
                        "&:before": {
                          borderBottomColor: "#fff !important",
                        },
                        "&:hover:not(.Mui-disabled):before": {
                          borderBottomColor: "#fff !important",
                        },
                        "&:after": {
                          borderBottomColor: "#fff !important",
                        },

                        /* actual input text */
                        "& input": {
                          fontSize: "13px",
                          padding: "4px 0",
                          color: "#fff !important",
                          WebkitTextFillColor: "#fff !important",
                        },

                        /* placeholder */
                        "& input::placeholder": {
                          color: "#fff !important",
                          opacity: 0.8,
                        },
                      },
                    },
                    sx: {
                      "& .MuiInputLabel-root": {
                        color: "#fff !important",
                        fontSize: "12px",
                      },
                      "& .MuiIconButton-root": {
                        color: "#fff",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const rowDate = dayjs(row.getValue(id)).format("YYYY-MM-DD");
          return rowDate === filterValue;
        },
      },
      {
        accessorKey: "holiday_name",
        header: "Holiday Name",
        size: 120,
        grow: true,
        Cell: ({ cell }) => (
          <Box display="flex" alignItems="center" gap={1}>
            <MDTypography variant="button" fontWeight="medium">
              {cell.getValue()}
            </MDTypography>
          </Box>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        filterVariant: "select",
        filterSelectOptions: [
          { label: "All", value: "" },
          { label: "Active", value: "1" },
          { label: "Inactive", value: "0" },
        ],
        muiFilterTextFieldProps: {
          select: true,
          SelectProps: {
            native: true,
            sx: {
              color: "#fff !important",
              "& .MuiNativeSelect-select": {
                color: "#fff !important",
                backgroundColor: "transparent",
              },
              "& .MuiNativeSelect-icon": {
                color: "#fff !important",
              },
              "& option": {
                color: "#000",
                backgroundColor: "#fff",
              },
            },
          },
        },
        Cell: ({ row }) => (
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <Switch
              checked={row.original.status === 1 || row.original.status === "1"}
              onChange={() => handleStatusChange(row.original.id, row.original.status)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#4caf50",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#4caf50",
                },
              }}
            />
            <Chip
              label={row.original.status === 1 || row.original.status === "1" ? "Active" : "Inactive"}
              size="small"
              color={row.original.status === 1 || row.original.status === "1" ? "success" : "default"}
              sx={{ fontWeight: 500, minWidth: "70px" }}
            />
          </Box>
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableColumnFilter: false,
        muiTableBodyCellProps: {
          align: "center",
        },
        enableSorting: false,
        Cell: ({ row }) => (
          <Tooltip title="Edit Holiday" arrow>
            <IconButton
              onClick={() => handleEdit(row.original)}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s",
              }}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        {/* Header Section */}
        <Fade in timeout={500}>
          <MDBox mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      backgroundColor: "#004030",
                      borderRadius: "12px",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CalendarTodayIcon sx={{ color: "#fff", fontSize: "28px" }} />
                  </Box>
                  <Box>
                    <MDTypography variant="h3" fontWeight="bold" color="dark">
                      Holiday Management
                    </MDTypography>
                    {/* <MDTypography variant="button" color="text" fontWeight="regular">
                      Manage and organize company holidays
                    </MDTypography> */}
                  </Box>
                </Box>
              </Grid>

              {/* Statistics Cards */}
              {/* <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: "#e3f2fd",
                        borderRadius: "12px",
                        border: "1px solid #bbdefb",
                      }}
                    >
                      <MDTypography variant="h4" fontWeight="bold" color="info">
                        {stats.total}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total Holidays
                      </MDTypography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: "#e8f5e9",
                        borderRadius: "12px",
                        border: "1px solid #c8e6c9",
                      }}
                    >
                      <MDTypography variant="h4" fontWeight="bold" color="success">
                        {stats.active}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Active
                      </MDTypography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        backgroundColor: "#fff3e0",
                        borderRadius: "12px",
                        border: "1px solid #ffe0b2",
                      }}
                    >
                      <MDTypography variant="h4" fontWeight="bold" color="warning">
                        {stats.upcoming}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Upcoming
                      </MDTypography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid> */}
            </Grid>
          </MDBox>
        </Fade>

        {/* Form Card */}
        <Zoom in timeout={600}>
          <Card
            sx={{
              mb: 4,
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              overflow: "visible",
            }}
          >
            <MDBox
              p={3}
              sx={{
                background: isEditMode
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #004030 0%, #006d4d 100%)",
                borderRadius: "16px 16px 0 0",
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      borderRadius: "10px",
                      p: 1,
                      display: "flex",
                    }}
                  >
                    {isEditMode ? (
                      <Edit sx={{ color: "#fff", fontSize: "24px" }} />
                    ) : (
                      <CalendarTodayIcon sx={{ color: "#fff", fontSize: "24px" }} />
                    )}
                  </Box>
                  <MDTypography variant="h5" fontWeight="bold" color="white">
                    {isEditMode ? "Edit Holiday" : "Add New Holiday"}
                  </MDTypography>
                </Box>
                {isEditMode && (
                  <Chip
                    label="Edit Mode"
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      fontWeight: "bold",
                    }}
                  />
                )}
              </Box>
            </MDBox>

            <MDBox p={4}>
              <Grid container spacing={3}>
                {/* Holiday Name */}
                <Grid item xs={12} sm={4} md={4}>
                  <MDTextField
                    label="Holiday Name *"
                    placeholder="e.g., New Year's Day"
                    fullWidth
                    size="small"
                    value={holidayName}
                    onChange={(value) => {
                      setHolidayName(value);
                      if (errors.holidayName) {
                        setErrors({ ...errors, holidayName: "" });
                      }
                    }}
                    error={!!errors.holidayName}
                    helperText={errors.holidayName || " "}
                    FormHelperTextProps={{ sx: { minHeight: "18px" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "48px",
                        borderRadius: "10px",
                      },
                    }}
                  />
                </Grid>

                {/* Holiday Date */}
                <Grid item xs={12} sm={4} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Holiday Date *"
                      value={holidayDate}
                      format="DD-MM-YYYY"
                      onChange={(newValue) => {
                        setHolidayDate(newValue);
                        if (errors.holidayDate) {
                          setErrors({ ...errors, holidayDate: "" });
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: !!errors.holidayDate,
                          helperText: errors.holidayDate || " ",
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              height: "48px",
                              borderRadius: "10px",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                {/* Status */}
                {/* Status */}
                <Grid item xs={12} sm={4} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: "48px",
                      border: "1px solid #d2d6da",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      backgroundColor: status === 1 ? "#e8f5e9" : "#f5f5f5",
                    }}
                  >
                    <MDTypography variant="button" fontWeight="medium">
                      Status
                    </MDTypography>

                    <Box display="flex" alignItems="center" gap={1}>
                      <Switch
                        checked={status === 1}
                        onChange={(e) => setStatus(e.target.checked ? 1 : 0)}
                      />
                      <Chip
                        label={status === 1 ? "Active" : "Inactive"}
                        size="small"
                        color={status === 1 ? "success" : "default"}
                        sx={{ minWidth: 70 }}
                      />
                    </Box>
                  </Paper>

                  {/* Helper spacer */}
                  <Box sx={{ minHeight: "18px" }} />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <MDBox display="flex" justifyContent="center" gap={2} mt={4}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={clearForm}
                  sx={{
                    borderRadius: "10px",
                    px: 4,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    borderWidth: "2px",
                    "&:hover": {
                      borderWidth: "2px",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {isEditMode ? "Cancel Edit" : "Clear Form"}
                </MDButton>
                <MDButton
                  variant="gradient"
                  color={isEditMode ? "info" : "success"}
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    borderRadius: "10px",
                    px: 5,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  {loading ? "Processing..." : isEditMode ? "Update Holiday" : "Add Holiday"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Zoom>

        {/* Table Card */}
        <Fade in timeout={800}>
          <Card
            sx={{
              borderRadius: "16px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <MDBox p={3}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: "10px",
                    p: 1.5,
                    display: "flex",
                  }}
                >
                  <EventNote sx={{ color: "#004030", fontSize: "24px" }} />
                </Box>
                <Box>
                  <MDTypography variant="h5" fontWeight="bold" color="dark">
                    Holidays List
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    View and manage all holidays
                  </MDTypography>
                </Box>
              </Box>

              <MaterialReactTable
                columns={columns}
                data={holidays}
                state={{
                  isLoading: loading,
                  columnFilters,
                  globalFilter,
                }}
                onColumnFiltersChange={setColumnFilters}
                onGlobalFilterChange={setGlobalFilter}
                manualFiltering={true}
                enableDensityToggle={false}
                enableGlobalFilter={true}
                enableColumnFilters={true}
                enableFullScreenToggle={false}
                enableRowNumbers
                enableStickyHeader
                enableRowStacking
                layoutMode="grid"
                enableColumnResizing={true}
                enableColumnActions={false}
                muiTableProps={{
                  sx: {
                    minWidth: "700px",
                  },
                }}
                muiTablePaperProps={{
                  elevation: 0,
                  sx: {
                    borderRadius: "12px",
                  },
                }}
                muiTableHeadProps={{
                  sx: {
                    backgroundColor: "#004030",
                  },
                }}
                muiTableHeadCellProps={{
                  align: "center",
                  sx: {
                    backgroundColor: "#004030",
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: "13px",
                    py: 2,
                    textAlign: "center",
                    justifyContent: "center",
                    "& .MuiBox-root": {
                      justifyContent: "center",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                    "& .MuiTableSortLabel-icon": {
                      color: "#fff !important",
                      justifyContent: "center",
                    },
                    /* Column Filter Styling */
                    "& .MuiInput-underline:before": {
                      borderBottomColor: "rgba(255, 255, 255, 0.7) !important",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#fff !important",
                    },
                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                      borderBottomColor: "#fff !important",
                    },
                    "& .MuiInputBase-input": {
                      color: "#fff !important",
                      fontSize: "12px",
                      "&::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#fff !important",
                      fontSize: "12px",
                    },
                    "& .MuiIconButton-root": {
                      color: "#fff !important",
                    },
                  },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  sx: {
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                    transition: "all 0.2s",
                  },
                })}
                muiTableBodyCellProps={{
                  sx: {
                    padding: "12px 16px",
                  },
                }}
                muiSearchTextFieldProps={{
                  placeholder: "Search holidays...",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  },
                  variant: "outlined",
                  size: "small",
                }}
              />
            </MDBox>
          </Card>
        </Fade>
      </MDBox>

      {/* Loading Backdrop */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <Box textAlign="center">
          <CircularProgress color="inherit" size={60} />
          <MDTypography variant="h6" color="white" mt={2}>
            Processing...
          </MDTypography>
        </Box>
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            color: "white !important",
            borderRadius: "10px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default HolidayAdd;
